import type { Logger } from '@zg-rentals/logger-base';

import type { MonitorPlugin } from './plugin';
import type { MonitorReporter } from './reporter';
import type { StatController } from './statController';

export interface MonitorInterface<PluginType, ReporterType> {
  logger?: Logger;
  plugins?: Array<PluginType>;
  reporters?: Array<ReporterType>;
  ignoreStart?: boolean;
}

export interface MetricAlert {
  periodMs: number;
  minThreshold?: number;
  maxThreshold?: number;
  alertFrequencyMinutes: number;
  customEmails?: Array<string>;
  metricName?: string;
  metricType?: 'GAUGE' | 'COUNT';
}

export interface MetricOptions {
  tags?: Record<string, unknown>;
  alert?: MetricAlert;
  [key: string]: unknown;
}

export interface CountOptions {
  name: string;
  amount?: number;
  options?: MetricOptions;
}

export interface MeasureOptions {
  name: string;
  amount: number;
  options?: MetricOptions;
}
export interface Monitor {
  count: (opts: CountOptions) => Promise<void>;
  measurementWithGauges: (opts: MeasureOptions) => Promise<void>;
  gauges: (opts: MeasureOptions) => Promise<void>;
  error: ({ error }: { error: Error }) => Promise<void>;
}

export class BaseMonitor<PluginType extends MonitorPlugin, ReporterType extends MonitorReporter> implements Monitor {
  public plugins: Array<PluginType>;
  public reporters: Array<ReporterType>;
  public stats: Record<string, StatController>;

  protected logger?: Logger;

  constructor({ logger, plugins = [], reporters = [], ignoreStart }: MonitorInterface<PluginType, ReporterType> = {}) {
    this.logger = logger;
    this.plugins = plugins;
    this.reporters = reporters;
    this.stats = {};

    this._initializeReporters = this._initializeReporters.bind(this);
    this._trackMonitorStart = this._trackMonitorStart.bind(this);
    this._initializePlugins = this._initializePlugins.bind(this);
    this.count = this.count.bind(this);
    this.gauges = this.gauges.bind(this);
    this.measurementWithGauges = this.measurementWithGauges.bind(this);
    this.error = this.error.bind(this);

    this._initializeReporters();
    if (!ignoreStart) {
      this._trackMonitorStart();
    }
    this._initializePlugins();
  }

  private _initializeReporters() {
    this.reporters.forEach((reporter) => {
      reporter.onInitialize(this.logger);
      if (reporter.stats) {
        this.stats[reporter.reporterName] = reporter.stats;
      }
    });
  }

  private _initializePlugins() {
    this.plugins.forEach((plugin) => {
      plugin.onInitialize(this.count.bind(this), this.measurementWithGauges.bind(this), this.logger);
      if (plugin.stats) {
        this.stats[plugin.pluginName] = plugin.stats;
      }
    });
  }

  private async _trackMonitorStart() {
    await this.count({ name: 'Monitor started' });
  }

  async count(...counts: Array<CountOptions>) {
    const countPromises: Array<Promise<void>> = [];
    counts.forEach(({ name, amount = 1, options }) => {
      this.reporters.forEach((reporter) => {
        countPromises.push(
          reporter.count({
            name,
            amount,
            options,
          }),
        );
      });
    });
    await Promise.all(countPromises);
  }

  async gauges(...measures: Array<MeasureOptions>) {
    const measurePromises: Array<Promise<void>> = [];
    measures.forEach(({ name, amount, options }) => {
      this.reporters.forEach((reporter) => {
        measurePromises.push(
          reporter.gauges({
            name,
            amount,
            options,
          }),
        );
      });
    });
    await Promise.all(measurePromises);
  }

  async measurementWithGauges(...measures: Array<MeasureOptions>) {
    const measureWithPercentilesPromises: Array<Promise<void>> = [];
    measures.forEach(({ name, amount, options }) => {
      this.reporters.forEach((reporter) => {
        measureWithPercentilesPromises.push(
          reporter.measurementWithGauges({
            name,
            amount,
            options,
          }),
        );
      });
    });
    await Promise.all(measureWithPercentilesPromises);
  }

  async error({ error, ...rest }: { error: Error } & Record<string, unknown>) {
    await Promise.all(
      this.reporters.map((reporter) => {
        return reporter.onError({ error, ...rest });
      }),
    );
  }
}
